import type { LayerName } from '$lib/components/map/map-styles';

interface MapPreferences {
	mapStyleLayerName?: LayerName;
	showTrafficLayer?: boolean;
	showLezLayer?: boolean;
	showPoiMarkerWithDetails?: boolean;
	hasAutomaticallyShowPoiMarkerWithDetails?: true;
}

type MapPreferencesKey = keyof MapPreferences;

type MapPreferencesValue<K extends keyof MapPreferences> = Exclude<MapPreferences[K], undefined>;

const preferencesKeys: MapPreferencesKey[] = [
	'mapStyleLayerName',
	'showTrafficLayer',
	'showLezLayer',
	'showPoiMarkerWithDetails',
	'hasAutomaticallyShowPoiMarkerWithDetails',
];

const LOCAL_STORAGE_KEY = 'mapSettingsUserPreferences';

export function createMapSettingsUserPreferencesLocalStorage(isBrowser: boolean) {
	function getPreferencesFromLocalStorage(): MapPreferences | null {
		// Use null instead of undefined to indicate 'no value' to match localeStorage behavior returning null when the key is not found
		const preferences: MapPreferences = {};
		const preferencesFromLocalStorageRaw = localStorage.getItem(LOCAL_STORAGE_KEY);

		if (typeof preferencesFromLocalStorageRaw === 'string') {
			// Since it's a user input, data must be obtain safely from local storage
			try {
				const preferencesFromLocalStorageParsed = JSON.parse(preferencesFromLocalStorageRaw);

				if (Object.keys(preferencesFromLocalStorageParsed).length > 0) {
					for (const key of preferencesKeys) {
						const value = preferencesFromLocalStorageParsed[key];
						if (typeof value !== 'undefined' && value !== null) {
							preferences[key] = preferencesFromLocalStorageParsed[key];
						}
					}
				}
			} catch {
				localStorage.removeItem(LOCAL_STORAGE_KEY);
				return null;
			}
		} else {
			return null;
		}

		return preferences;
	}

	return {
		get<K extends MapPreferencesKey>(key: K): MapPreferences[K] {
			if (!isBrowser) {
				return undefined;
			}
			const preferencesFromLocalStorage = getPreferencesFromLocalStorage();
			return preferencesFromLocalStorage ? preferencesFromLocalStorage[key] : undefined;
		},
		set<K extends MapPreferencesKey>(key: K, value: MapPreferencesValue<K>) {
			if (!isBrowser) {
				return undefined;
			}
			const preferencesFromLocalStorage = getPreferencesFromLocalStorage();
			const newPreferences = {
				...(preferencesFromLocalStorage || {}),
				[key]: value,
			};
			localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newPreferences));
		},
		has<K extends MapPreferencesKey>(key: K): boolean {
			if (!isBrowser) {
				return false;
			}

			const preferencesFromLocalStorage = getPreferencesFromLocalStorage();
			return preferencesFromLocalStorage ? key in preferencesFromLocalStorage : false;
		},
	};
}
